import React, {useState} from 'react';
import {Form, Formik} from 'formik';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import {toast} from 'react-toastify';
import {
    BrandingFormValues,
    BrandingTemplateDialogProps,
    SetFieldValueFunction
} from "../../../../entity/props/title/branding-template-dialog-props";
import {useService} from "../../../../infrastructure/service/service";

import '../../../../assets/css/title/branding-template-dialog.css';

export const BrandingTemplateDialog = (props: BrandingTemplateDialogProps): JSX.Element => {
    const {documentService, userService, titleService, brandingTemplateService} = useService();

    const [isSaving, setIsSaving] = useState<boolean>(false);

    const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>, setFieldValue: SetFieldValueFunction): Promise<void> => {
        const file = event.target.files?.[0];
        if (file) {
            const user = await userService.getCurrentUser();
            const title = titleService.getTitle();
            
            if (!user?.organizationID) {
                toast.error('User organization information is missing');
                return;
            }
            
            if (!title) {
                toast.error('No title selected for logo upload');
                return;
            }

            const logoUrl = await documentService.uploadLogo(user.organizationID, file, title.id);
            if (logoUrl) {
                setFieldValue('logo', logoUrl);
                toast.success('Logo uploaded successfully');
            } else {
                toast.error('Failed to upload logo');
            }
        }
    };

    const handleSave = async (values: BrandingFormValues): Promise<void> => {
        setIsSaving(true);

        const template = brandingTemplateService.createBrandingTemplate(values, props.editingTemplate);

        await props.onSave(template);
        props.onClose();
        setIsSaving(false);
    };


    const removeLogo = (setFieldValue: SetFieldValueFunction): void => setFieldValue('logo', '');

    const handleValidate = (values: BrandingFormValues): Record<string, string> => {
        const errors: Record<string, string> = {};

        if (!values.name || values.name.trim() === '') {
            errors.name = 'Template name is required';}

        if (!values.companyName || values.companyName.trim() === '') {
            errors.companyName = 'Company name is required';}

        if (!values.companyAddress || values.companyAddress.trim() === '') {
            errors.companyAddress = 'Company address is required';}

        if (values.email && values.email.trim() !== '') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(values.email)) {
                errors.email = 'Please enter a valid email address';}
        }

        if (values.website && values.website.trim() !== '') {
            try {
                new URL(values.website.startsWith('http') ? values.website : `https://${values.website}`);
            } catch {
                errors.website = 'Please enter a valid website URL';
            }
        }

        return errors;
    };

    return (
        <Dialog
            open={props.isOpen}
            onClose={props.onClose}
            maxWidth='md'
            fullWidth
            className='branding-config-dialog zitles-dialog'>
            <Formik
                initialValues={brandingTemplateService.getBrandingFormValues(props.editingTemplate)}
                validate={handleValidate}
                onSubmit={handleSave}
                enableReinitialize={true}>
                {({values, errors, touched, handleChange, handleBlur, isValid, setFieldValue}) => (
                    <Form>
                        <DialogTitle className='zitles-dialog-title'>
                            <span className='branding-dialog-title-text'>
                                {props.editingTemplate ? 'Edit' : 'Create'} Branding Template
                            </span>
                            <IconButton id='btn-dialog-close' onClick={props.onClose}
                                        className='branding-dialog-close-button'>
                                <CloseIcon className='dialog-icon'/>
                            </IconButton>
                        </DialogTitle>

                        <DialogContent dividers className='branding-dialog-content'>
                            <Box className='branding-form-container'>

                                <Card elevation={0} className='branding-card'>
                                    <CardHeader
                                        className='branding-card-header'
                                        title='Template Details'
                                        subheader='Enter template name and identifier'/>
                                    <CardContent className='branding-card-content'>
                                        <TextField
                                            id='input-template-name'
                                            fullWidth
                                            size='small'
                                            label='Template Name *'
                                            placeholder='e.g., Default Template'
                                            required
                                            name='name'
                                            value={values.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.name && !!errors.name}
                                            helperText={touched.name && typeof errors.name === 'string' ? errors.name : ''}
                                            className='branding-textfield'/>
                                    </CardContent>
                                </Card>

                                <Card elevation={0} className='branding-card'>
                                    <CardHeader
                                        className='branding-card-header'
                                        title='Company Information'
                                        subheader='Enter your business details and contact information'/>
                                    <CardContent className='branding-card-content'>
                                        <Box className='branding-field-column'>
                                            <TextField
                                                id='input-company-name'
                                                fullWidth
                                                size='small'
                                                label='Company Name *'
                                                placeholder='Your Company Name'
                                                required
                                                name='companyName'
                                                value={values.companyName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.companyName && !!errors.companyName}
                                                helperText={touched.companyName && typeof errors.companyName === 'string' ? errors.companyName : ''}
                                                className='branding-textfield'/>

                                            <TextField
                                                id='input-company-address'
                                                fullWidth
                                                size='small'
                                                label='Company Address *'
                                                multiline
                                                rows={3}
                                                placeholder='123 Business Street&#10;City, State 12345'
                                                required
                                                name='companyAddress'
                                                value={values.companyAddress}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.companyAddress && !!errors.companyAddress}
                                                helperText={touched.companyAddress && typeof errors.companyAddress === 'string' ? errors.companyAddress : ''}
                                                className='branding-textfield branding-textarea'/>

                                            <Box className='branding-field-row'>
                                                <TextField
                                                    id='input-phone'
                                                    fullWidth
                                                    size='small'
                                                    label='Phone'
                                                    placeholder='(555) 123-4567'
                                                    name='phone'
                                                    value={values.phone}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.phone && !!errors.phone}
                                                    helperText={touched.phone && typeof errors.phone === 'string' ? errors.phone : ''}
                                                    className='branding-textfield'/>

                                                <TextField
                                                    id='input-email'
                                                    fullWidth
                                                    size='small'
                                                    label='Email'
                                                    type='email'
                                                    placeholder='contact@company.com'
                                                    name='email'
                                                    value={values.email}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.email && !!errors.email}
                                                    helperText={touched.email && typeof errors.email === 'string' ? errors.email : ''}
                                                    className='branding-textfield'/>
                                            </Box>

                                            <TextField
                                                id='input-website'
                                                fullWidth
                                                size='small'
                                                label='Website'
                                                placeholder='www.company.com'
                                                name='website'
                                                value={values.website}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.website && !!errors.website}
                                                helperText={touched.website && typeof errors.website === 'string' ? errors.website : ''}
                                                className='branding-textfield'/>
                                        </Box>
                                    </CardContent>
                                </Card>

                                <Card elevation={0} className='branding-card'>
                                    <CardHeader
                                        className='branding-card-header'
                                        title='Company Logo'
                                        subheader='Upload your company logo for invoice branding PNG or JPEG/JPG only'/>
                                    <CardContent className='branding-card-content'>
                                        <Box className='branding-upload-section'>
                                            <Typography className='branding-upload-label'>
                                                Upload Logo
                                            </Typography>

                                            <Box className='branding-upload-buttons'>
                                                <Box className='branding-upload-button-wrapper'>
                                                    <input
                                                        id='input-logo-file'
                                                        type='file'
                                                        accept='image/png,image/jpeg,image/jpg,.png,.jpg,.jpeg'
                                                        onChange={(e) => handleLogoUpload(e, setFieldValue)}
                                                        className='branding-file-input'/>
                                                    <Button
                                                        id='btn-choose-file'
                                                        variant='outlined'
                                                        startIcon={<UploadIcon/>}
                                                        className='branding-button branding-upload-button'>
                                                        Choose File
                                                    </Button>
                                                </Box>

                                                {
                                                    values.logo &&
                                                        <Button
                                                            id='btn-remove-logo'
                                                            variant='outlined'
                                                            size='small'
                                                            onClick={() => removeLogo(setFieldValue)}
                                                            className='branding-button branding-remove-button'
                                                            startIcon={<DeleteIcon/>}>
                                                            Remove
                                                        </Button>
                                                }
                                            </Box>

                                            {
                                                values.logo &&
                                                    <Box className='branding-logo-preview'>
                                                        <img
                                                            src={values.logo}
                                                            alt='Logo preview'
                                                            className='branding-logo-image'/>
                                                    </Box>
                                            }
                                        </Box>
                                    </CardContent>
                                </Card>

                                <Card elevation={0} className='branding-card'>
                                    <CardHeader
                                        className='branding-card-header'
                                        title='Footer Settings'
                                        subheader='Add custom footer text for your invoices'/>
                                    <CardContent className='branding-card-content'>
                                        <TextField
                                            id='input-footer-text'
                                            fullWidth
                                            size='small'
                                            label='Footer Text'
                                            multiline
                                            rows={2}
                                            placeholder='Thank you for your business!'
                                            name='footerText'
                                            value={values.footerText}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.footerText && !!errors.footerText}
                                            helperText={touched.footerText && typeof errors.footerText === 'string' ? errors.footerText : ''}
                                            className='branding-textfield branding-textarea'/>
                                    </CardContent>
                                </Card>
                            </Box>
                        </DialogContent>

                        <Divider className='branding-divider'/>
                        <DialogActions className='branding-dialog-actions action-btn-container'>
                            <Button
                                id='btn-branding-cancel'
                                onClick={props.onClose}
                                disabled={isSaving}
                                className='branding-button branding-cancel-button zitles-button cancel'>
                                Cancel
                            </Button>
                            <Button
                                id='btn-branding-submit'
                                type='submit'
                                disabled={!isValid || isSaving}
                                className={`zitles-button outlined ${(!isValid || isSaving) ? 'branding-disabled disable' : ''}`}
                                startIcon={isSaving ? <CircularProgress size={16} color='inherit'/> : <SaveIcon/>}>
                                {isSaving ? 'Saving...' : (props.editingTemplate ? 'Update' : 'Create')} Template
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};